import { faRobot } from "@fortawesome/pro-regular-svg-icons";
import { Dropdown, Icon, Tag } from "@intility/bifrost-react";
import { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { ClientListItem } from "../../api/types";
import { clientPath } from "../../lib/clientPath";
import { GoToTag } from "../GoToTag";

// The width the tags may fill before the rest collapse into a "+N" tag.
const MAX_WIDTH = 280;
const GAP = 8;

const labelStyle = {
  display: "block",
  maxWidth: 140,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

// Laid out but never seen or reached: the tags' real widths are read from it.
const measureStyle = {
  display: "flex",
  gap: GAP,
  pointerEvents: "none",
  position: "absolute",
  visibility: "hidden",
  width: "max-content",
} as const;

// How many tags fit in MAX_WIDTH next to a "+N" tag of `overflowWidth`.
function countThatFit(widths: number[], overflowWidth: number): number {
  const total = widths.reduce((sum, w, i) => sum + w + (i ? GAP : 0), 0);
  if (total <= MAX_WIDTH) return widths.length;

  let used = 0;
  let count = 0;
  for (const w of widths) {
    const next = used + (count ? GAP : 0) + w;
    if (next + GAP + overflowWidth > MAX_WIDTH) break;
    used = next;
    count++;
  }
  return count;
}

export function ClientTags({ clients }: { clients: ClientListItem[] }) {
  const navigate = useNavigate();
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(clients.length);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const row = measureRef.current;
    if (!row) return;

    const measure = () => {
      const widths = Array.from(
        row.children,
        (el) => (el as HTMLElement).offsetWidth,
      );
      // The last child is the widest possible "+N" tag.
      const overflowWidth = widths.pop() ?? 0;
      setVisibleCount(countThatFit(widths, overflowWidth));
    };

    measure();
    // Web fonts arriving later change the tag widths.
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  const goTo = (client: ClientListItem) => navigate(clientPath(client));
  const shown = clients.slice(0, visibleCount);
  const hidden = clients.slice(visibleCount);

  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        gap: GAP,
        maxWidth: MAX_WIDTH,
        position: "relative",
      }}
    >
      {shown.map((client) => (
        <GoToTag
          key={client.id}
          kind="client"
          onGo={() => goTo(client)}
          title={client.name}
        >
          <span style={labelStyle}>
            <Icon icon={faRobot} marginRight />
            {client.name}
          </span>
        </GoToTag>
      ))}

      {hidden.length > 0 && (
        <Dropdown
          visible={open}
          onHide={() => setOpen(false)}
          placement="bottom-start"
          content={
            // The dropdown renders in a portal, but React still bubbles its
            // clicks to the table row, which would open the platform.
            // biome-ignore lint/a11y/useKeyWithClickEvents: only stops propagation.
            // biome-ignore lint/a11y/noStaticElementInteractions: only stops propagation.
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                alignItems: "flex-start",
                display: "flex",
                flexDirection: "column",
                gap: GAP,
                padding: GAP,
              }}
            >
              {hidden.map((client) => (
                <GoToTag
                  key={client.id}
                  kind="client"
                  onGo={() => goTo(client)}
                  title={client.name}
                >
                  <span style={labelStyle}>
                    <Icon icon={faRobot} marginRight />
                    {client.name}
                  </span>
                </GoToTag>
              ))}
            </div>
          }
        >
          <Tag
            aria-expanded={open}
            aria-haspopup="true"
            aria-label={`${hidden.length} more clients`}
            onClick={() => setOpen((v) => !v)}
          >
            +{hidden.length}
          </Tag>
        </Dropdown>
      )}

      <div aria-hidden ref={measureRef} style={measureStyle}>
        {clients.map((client) => (
          <Tag key={client.id} onClick={() => {}} tabIndex={-1}>
            <span style={labelStyle}>
              <Icon icon={faRobot} marginRight />
              {client.name}
            </span>
          </Tag>
        ))}
        <Tag onClick={() => {}} tabIndex={-1}>
          +{clients.length}
        </Tag>
      </div>
    </div>
  );
}
